import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ClubBook, PlanView, ProgressEntry } from "@pagemates/shared";
import { api, describeError } from "../../lib/api";
import { Avatar, EmptyState, Spinner } from "../../components/ui";
import { Envelope, FinishCeremony, LeaveNoteSheet, MomentCard, PredictionCard, PredictionsRow, useMoment, usePredictions } from "../../components/Moments";
import { Cover, authorsOf } from "../../components/BookBits";
import { CalendarIcon, CheckIcon, ChevronIcon, ClubIcon, MoreIcon, NoteIcon, SyncIcon, TalkIcon, VoteIcon } from "../../components/Icons";
import { ProgressEditor } from "../../components/ProgressEditor";
import { useClub } from "./ClubLayout";
import { MiniNotice } from "../../components/Noticeboard";
import { PickBook } from "../../components/PickBook";
import { StragglerCard } from "../../components/FinishCards";
import { DatesEditor } from "../../components/DatesEditor";
import { PlanSheet } from "../../components/PlanSheet";
import { amountBetween, duration, pageAt, paceLabel, secondsAt, type PosCtx } from "../../lib/format";

/*
 * The Reading tab (PLAN.md §2 Screens, redesigned 2026-11-03): the book as a
 * poster with its own cover blurred behind it, your block (where you are, the
 * pace, the editor, a note line), then everyone else on one shared track.
 * Tapping a face shows that reader under the track; faces that would overlap
 * pile up with a count, so the same track works for a club of three or three
 * hundred. The aside rows are fills, not outlined cards.
 */

const DAY = 86_400_000;
const dateShort = (iso: string) => new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });

function countdown(iso: string): string {
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / DAY);
  if (days < -1) return `${-days} days ago`;
  if (days === -1) return "yesterday";
  if (days === 0) return "today";
  if (days === 1) return "tomorrow";
  return `in ${days} days`;
}

export function NowTab() {
  const { clubId, club } = useClub();
  const c = club.data!;
  const me = useQuery({ queryKey: ["me"], queryFn: api.me });
  const books = useQuery({ queryKey: ["books", clubId], queryFn: () => api.clubBooks(clubId) });
  const qc = useQueryClient();
  const linked = me.data?.hardcover.linked && me.data.hardcover.status === "ok";
  useEffect(() => {
    if (!linked) return;
    void api.hardcoverSync().then((r) => { if (r.updated > 0) void qc.invalidateQueries({ queryKey: ["books", clubId] }); }).catch(() => undefined);
  }, [linked, clubId, qc]);

  if (books.isPending) return <main className="page"><Spinner /></main>;
  if (books.isError) return <main className="page"><p className="error">{describeError(books.error)}</p></main>;
  const cur = books.data.current;
  const isAdmin = c.myRole !== "member";

  const meetingBook = books.data.current ?? books.data.finished[0] ?? null;
  const at = c.meeting ? new Date(c.meeting.at) : null;
  const tile = at && <span className="tile" aria-hidden="true"><i>{at.toLocaleDateString(undefined, { month: "short" })}</i><b>{at.getDate()}</b></span>;
  const meetingRow = (c.meeting || (isAdmin && meetingBook)) && (
    meetingBook ? (
      <Link to="/clubs/$clubId/meeting" params={{ clubId }} search={{ book: meetingBook.id }} className="quiet link">
        {tile ?? <CalendarIcon />}
        <div style={{ minWidth: 0 }}>
          <b>{at ? `${at.toLocaleDateString(undefined, { weekday: "short" })} ${at.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}${c.meeting!.location ? ` · ${c.meeting!.location}` : ""}` : "Meeting page"}</b>
          <div className="d">{c.meeting ? `${countdown(c.meeting.at)}${c.meeting.host ? ` · ${c.meeting.host.name} hosting` : ""}${coversText(books.data.current?.plan)}${c.meeting.rsvp.mine && !c.meeting.rsvp.mine.stale ? ` · you're ${{ going: "going", cant: "not going", maybe: "a maybe" }[c.meeting.rsvp.mine.status]}` : c.meeting.rsvp.mine?.stale ? " · date moved, still going?" : ""}` : "No date set · the agenda is here anyway"}</div>
        </div>
        <ChevronIcon className="go" />
      </Link>
    ) : (
      <div className="quiet">
        {tile}
        <div><b>{at!.toLocaleDateString(undefined, { weekday: "short" })} {at!.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}{c.meeting!.location ? ` · ${c.meeting!.location}` : ""}</b>
          <div className="d">{countdown(c.meeting!.at)}</div></div>
      </div>
    )
  );
  // The noticeboard in Around the club: the pinned notice while there is one, and what's new since your last visit.
  const board = c.board;
  const boardRows = [
    board.pinned && (
      <Link key="pin" to="/clubs/$clubId/club" params={{ clubId }} search={{ post: board.pinned.id }} className="quiet link nb-around">
        <MiniNotice />
        <div style={{ minWidth: 0 }}><b className="clip">Pinned: {board.pinned.firstLine}</b><div className="d">{board.pinned.author.name} · Noticeboard{board.pinned.until ? ` · until ${new Date(board.pinned.until).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : ""}</div></div>
        <ChevronIcon className="go" />
      </Link>
    ),
    board.newPosts > 0 && board.latest && (
      <Link key="new" to="/clubs/$clubId/club" params={{ clubId }} className="quiet link nb-around">
        <Avatar name={board.latest.author.name} image={board.latest.author.image} />
        <div style={{ minWidth: 0 }}><b>{board.newPosts} new on the noticeboard</b><div className="d clip">{board.latest.author.name}: {board.latest.firstLine}</div></div>
        <ChevronIcon className="go" />
      </Link>
    ),
  ].filter(Boolean);
  const straggler = books.data.straggler && <StragglerCard clubId={clubId} s={books.data.straggler} />;
  // Finished books you're still in: their own section, each row to the panel where your progress lives.
  const stillReading = books.data.finished.filter((b) => b.mine && b.mine.touched && b.mine.status === "reading" && b.mine.percent < 100 && b.id !== books.data.straggler?.clubBookId);
  const stillSection = stillReading.length > 0 && (
    <section>
      <div className="sect"><h3>Still reading</h3><span className="count">{stillReading.length === 1 ? "on your own time" : `${stillReading.length} books`}</span></div>
      <div className="stack-sm" style={{ marginTop: 10 }}>
        {stillReading.map((b) => (
          <Link key={b.id} to="/clubs/$clubId/recap/$bookId" params={{ clubId, bookId: b.id }} className="quiet link">
            {b.book.coverUrl ? <span className="cover sm" style={{ width: 26, height: 39 }}><img src={b.book.coverUrl} alt="" /></span> : <TalkIcon />}
            <div style={{ minWidth: 0 }}>
              <b>{b.book.title}</b>
              <div className="d">{b.book.pages ? `p. ${pageAt(b.mine!.percent, b.book.pages)} of ${b.book.pages} · ` : ""}{b.mine!.percent}%{b.finishedAt ? ` · the club finished it ${dateShort(b.finishedAt)}` : ""}</div>
            </div>
            <ChevronIcon className="go" />
          </Link>
        ))}
      </div>
    </section>
  );

  const vote = books.data.vote;
  // The vote row leads with the cover of whichever book is ahead.
  const leading = vote ? [...vote.entries].sort((a, b) => b.points - a.points)[0] : null;
  const leadBook = leading && leading.points > 0 ? books.data.onDeck.find((b) => b.id === (vote?.winnerId ?? leading.clubBookId)) ?? null : null;
  const voteRow = vote ? (
    <Link to="/clubs/$clubId/shelf" params={{ clubId }} className="quiet link">
      {leadBook ? <Cover book={leadBook.book} size="sm" /> : <VoteIcon />}
      <div style={{ minWidth: 0 }}><b>{vote.status === "open" ? "Vote on the next book" : vote.winnerId ? "The vote is in" : "The vote tied"}</b>
        <div className="d">{vote.status === "open"
          ? `${leadBook ? `${leadBook.book.title} leads · ` : ""}${vote.closesAt ? `closes ${new Date(vote.closesAt).toLocaleDateString(undefined, { month: "short", day: "numeric" })}` : "open"} · ${vote.myRanks.length > 0 ? "you've voted" : "you haven't voted"} · ${vote.voted} of ${vote.members}`
          : vote.winnerId ? `${leadBook ? `${leadBook.book.title} · ` : ""}${isAdmin ? "start it from the shelf when the club is ready" : "an admin starts it from the shelf"}` : (isAdmin ? "Break the tie on the shelf" : "An admin breaks the tie")}</div></div>
      <ChevronIcon className="go" />
    </Link>
  ) : c.settings.pickPolicy === "vote" && isAdmin && (books.data.onDeck.length >= 2) && (!books.data.current || books.data.current.plan?.ended) ? (
    <Link to="/clubs/$clubId/shelf" params={{ clubId }} className="quiet link admin">
      <VoteIcon />
      <div><b>Open a vote on the next book</b><div className="d">Admins · the on-deck list becomes the ballot</div></div>
      <ChevronIcon className="go" />
    </Link>
  ) : books.data.turn && !books.data.current ? (
    <Link to="/clubs/$clubId/shelf" params={{ clubId }} className="quiet link">
      <Avatar name={books.data.turn.user.name} image={books.data.turn.user.image} />
      <div><b>{books.data.turn.user.id === me.data?.id ? "It's your turn to pick" : `It's ${books.data.turn.user.name}'s turn to pick`}</b><div className="d">From the on-deck list, on the shelf</div></div>
      <ChevronIcon className="go" />
    </Link>
  ) : null;

  if (!cur) {
    // A new club's first steps sit in the side column, each gone once it's done.
    const steps = isAdmin ? [
      c.memberCount === 1 && <Link key="invite" to="/clubs/$clubId/club" params={{ clubId }} search={{ invite: true }} className="quiet link step"><ClubIcon /><div><b>It's just you so far</b><div className="d">Invite someone</div></div><ChevronIcon className="go" /></Link>,
      !c.meeting && <Link key="meet" to="/clubs/$clubId/meeting" params={{ clubId }} className="quiet link step"><CalendarIcon /><div><b>Plan your first meeting</b><div className="d">A time and a place; the agenda fills in later</div></div><ChevronIcon className="go" /></Link>,
      !c.rules.trim() && <Link key="rules" to="/clubs/$clubId/club/settings" params={{ clubId }} className="quiet link step"><NoteIcon /><div><b>Say how the club works</b><div className="d">How often you meet, whether unfinished readers come</div></div><ChevronIcon className="go" /></Link>,
    ].filter(Boolean) : [];
    const around = Boolean(steps.length || voteRow || meetingRow || books.data.recentTalk || boardRows.length);
    return (
      <main className={`page now now-empty${around ? " has-aside" : ""}`}>
        <div className="now-main stack">
          <header className="club-head">
            <h1>{c.name}</h1>
            {c.description && <p className="tagline">{c.description}</p>}
          </header>
          {straggler}
          <EmptyReading clubId={clubId} isAdmin={isAdmin} canSuggest={isAdmin || c.settings.onDeckPolicy === "everyone"}
            first={books.data.finished.length === 0} deck={books.data.onDeck.length} />
          {stillSection}
        </div>
        {around && (
          <aside className="now-aside stack-sm">
            <div className="sect"><h3>Around the club</h3></div>
            {boardRows[0] /* the pinned notice is where meeting changes get seen */}
            {steps}
            {voteRow}
            {(c.meeting || !isAdmin) && meetingRow /* admins without a date get the "Plan your first meeting" step instead */}
            {books.data.recentTalk && <StillTalking clubId={clubId} t={books.data.recentTalk} />}
            {boardRows.slice(1)}
          </aside>
        )}
      </main>
    );
  }

  const mine = cur.progress.find((p) => p.id === me.data?.id);
  const finished = cur.progress.filter((p) => p.status === "finished").length;
  const showFinishCard = cur.myReader.status === "finished" && !cur.myReader.acknowledged;
  const dayN = cur.startedAt ? Math.max(1, Math.floor((Date.now() - new Date(cur.startedAt).getTime()) / DAY) + 1) : null;
  const plan = cur.plan!;
  const ctx: PosCtx = { pages: cur.book.pages, audioSeconds: cur.book.audioSeconds, myFormat: cur.myFormat };
  const planLine = planLineFor(plan, mine?.percent ?? 0, mine?.status ?? "reading", ctx);
  const others = cur.progress.length - (mine ? 1 : 0);
  return <Reading {...{ clubId, c, me: me.data, cur, mine, finished, showFinishCard, dayN, plan, ctx, planLine, others, isAdmin, straggler, stillSection, meetingRow, boardRows, voteRow, recentTalk: books.data.recentTalk }} />;
}

/** The Reading tab with a current book. Split out so the moment and predictions can be asked for with hooks. */
function Reading({ clubId, c, me, cur, mine, finished, showFinishCard, dayN, plan, ctx, planLine, others, isAdmin, straggler, stillSection, meetingRow, boardRows, voteRow, recentTalk }: {
  clubId: string; c: import("@pagemates/shared").Club; me: import("@pagemates/shared").Me | undefined; cur: NonNullable<import("@pagemates/shared").ClubBooks["current"]>; mine: ProgressEntry | undefined; finished: number; showFinishCard: boolean;
  dayN: number | null; plan: PlanView; ctx: PosCtx; planLine: ReturnType<typeof planLineFor>; others: number; isAdmin: boolean; straggler: React.ReactNode; stillSection: React.ReactNode;
  meetingRow: React.ReactNode; boardRows: React.ReactNode[]; voteRow: React.ReactNode; recentTalk: import("@pagemates/shared").ClubBooks["recentTalk"];
}) {
  const fullCtx: PosCtx = { ...ctx, landmarks: plan.landmarks };
  const moment = useMoment(clubId, cur.id, `${mine?.percent ?? 0}:${cur.myReader.status}`);
  const preds = usePredictions(clubId, cur.id, `${mine?.percent ?? 0}:${cur.myReader.status}`);

  return (
    <main className="page now">
      <div className="now-main stack">
        {showFinishCard ? <FinishCeremony clubId={clubId} book={cur} ctx={fullCtx} moment={moment.data ?? null} p={preds.data} />
          : moment.data && <MomentCard clubId={clubId} book={cur} ctx={fullCtx} moment={moment.data} />}
        {straggler}
        <section className="hero">
          {cur.book.coverUrl && <div className="art" aria-hidden="true"><img src={cur.book.coverUrl} alt="" /></div>}
          <Cover book={cur.book} size="lg" />
          <div className="grow" style={{ minWidth: 0 }}>
            <div className="t">{cur.book.url ? <a href={cur.book.url} target="_blank" rel="noopener">{cur.book.title}</a> : cur.book.title}</div>
            <div className="a">{authorsOf(cur.book)}{cur.myFormat === "audio" && cur.book.audioSeconds ? ` · ${duration(cur.book.audioSeconds)}` : cur.book.pages ? ` · ${cur.book.pages} pages` : ""}</div>
            <div className="meta">
              {planLine ? <span className={planLine.tone}>{planLine.node}</span>
                : cur.startedAt && <span>Started {dateShort(cur.startedAt)}{dayN ? <> · day <b className="n">{dayN}</b></> : null}</span>}
            </div>
          </div>
          {isAdmin && <AdminMenu clubId={clubId} book={cur} plan={plan} meetingAt={c.meeting?.at ?? null} usualPace={c.settings.usualPace} />}
        </section>

        {me && <YouBlock clubId={clubId} book={cur} mine={mine} statusAt={cur.myReader.statusAt} plan={plan} ctx={ctx} me={{ name: me.displayName ?? me.name, image: me.image }} />}
        {preds.data && <PredictionCard clubId={clubId} book={cur} ctx={fullCtx} p={preds.data} />}

        <section>
          <div className="sect"><h3>Where everyone else is</h3><span className="count">{others === 0 ? "just you so far" : finished > 0 ? `${finished} finished` : `${others} ${others === 1 ? "reader" : "readers"}`}</span></div>
          <Track entries={cur.progress} meId={me?.id} plan={plan} pages={cur.book.pages} />
        </section>
        {stillSection}
      </div>

      <div className="now-aside stack-sm">
        <div className="sect"><h3>Around the club</h3></div>
        {plan.ended && isAdmin && !c.meeting?.at && (
          <Link to="/clubs/$clubId/club" params={{ clubId }} className="quiet link admin">
            <CalendarIcon />
            <div><b>Set the next meeting</b><div className="d">The plan picks up from p. {plan.to} once there's a date.</div></div>
            <ChevronIcon className="go" />
          </Link>
        )}
        {boardRows[0]}
        {meetingRow}
        <TalkTeaser clubId={clubId} bookId={cur.id} />
        {preds.data && <PredictionsRow ctx={fullCtx} p={preds.data} />}
        {boardRows.slice(1)}
        {voteRow}
        {recentTalk && <StillTalking clubId={clubId} t={recentTalk} />}
      </div>
    </main>
  );
}

/** The reading lamp, in the tab icons' line weight; its light is on in dark mode (see .nightstand). */
function LampArt() {
  return (
    <svg className="lamp" viewBox="0 0 96 96" aria-hidden="true">
      <path className="light" d="M30 42 L66 42 L84 92 L12 92 Z" />
      <path className="shade" d="M34 18 H62 L70 42 H26 Z" />
      <path d="M48 42 V72" /><path d="M34 78 Q48 70 62 78" /><path d="M30 92 H66" /><path d="M48 72 V92" />
    </svg>
  );
}

/** What the meeting row says about the stretch. */
function coversText(plan: PlanView | undefined): string {
  if (!plan || !plan.custom) return "";
  if (plan.to === null) return " · covers the whole book";
  const lm = plan.landmarks.find((l) => l.page === plan.to);
  return ` · through ${lm ? lm.name : `p. ${plan.to}`}`;
}

/** The one line the hero says: the next landmark or the target, its date, and what's left for you. */
function planLineFor(plan: PlanView, myPercent: number, status: string, ctx: PosCtx): { node: React.ReactNode; tone: string } | null {
  if (plan.ended) {
    const met = plan.boundaries.length === 0 && plan.to !== null ? null : plan.boundaries.at(-1)?.metAt;
    return { node: <><b>Stretch ended at p. {plan.to}</b>{met ? ` · met ${dateShort(met)}` : ""}</>, tone: "" };
  }
  if (status !== "reading") return null;
  const next = plan.landmarks.find((l) => !l.beyond && l.percent > myPercent);
  const targetPercent = next ? next.percent : plan.toPercent;
  const targetDate = next ? next.date : plan.deadline;
  const name = next ? next.name : plan.to !== null ? `Through p. ${plan.to}` : "Finish";
  if (!targetDate && !next) return null; // nothing planned, no meeting: the old "started" line is better
  const left = next && ctx.pages && (ctx.myFormat ?? "pages") === "pages"
    ? `${Math.max(0, next.page - pageAt(myPercent, ctx.pages))} pages` : amountBetween(myPercent, targetPercent, ctx);
  return { node: <><b>{name}</b>{targetDate ? ` by ${dateShort(targetDate)}` : ""} · {myPercent >= targetPercent ? "you're there" : <><span className="n">{left}</span> to go</>}</>, tone: "plan" };
}

/** "p. 113 of 245 · 46%" split into the lead and the rest, in the reader's unit. */
function whereParts(percent: number, ctx: PosCtx): { lead: string; rest: string } {
  const f = ctx.myFormat ?? (ctx.pages ? "pages" : "percent");
  if (f === "pages" && ctx.pages) return { lead: `p. ${pageAt(percent, ctx.pages)}`, rest: `of ${ctx.pages} · ${percent}%` };
  if (f === "audio" && ctx.audioSeconds) return { lead: duration(secondsAt(percent, ctx.audioSeconds)), rest: `of ${duration(ctx.audioSeconds)} · ${percent}%` };
  return { lead: `${percent}%`, rest: ctx.pages ? `p. ${pageAt(percent, ctx.pages)} of ${ctx.pages}` : "" };
}

function ago(iso: string | null) {
  if (!iso) return "";
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.max(mins, 1)}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 48) return `${hrs}h ago`;
  return `${Math.round(hrs / 24)}d ago`;
}

/**
 * Your block: where you are and the pace on the first line, Update opens the
 * editor beneath, and the note line is the last line. Finished, given up and
 * sitting-out readers get their own first line.
 */
function YouBlock({ clubId, book, mine, statusAt, plan, ctx, me }: { clubId: string; book: ClubBook; mine: ProgressEntry | undefined; statusAt: string | null; plan: PlanView; ctx: PosCtx; me: { name: string; image: string | null } }) {
  const qc = useQueryClient();
  const percent = mine?.percent ?? 0;
  const status = mine?.status ?? "reading";
  const [open, setOpen] = useState(false);
  const refresh = async () => { await qc.invalidateQueries({ queryKey: ["books", clubId] }); await qc.invalidateQueries({ queryKey: ["discussion", clubId, book.id] }); };
  // Read it some other time: sit this one out, or come back to it.
  const rereading = useMutation({ mutationFn: (v: boolean) => api.setRereading(clubId, book.id, v), onSuccess: async () => { setOpen(false); await refresh(); } });
  const done = status === "finished", dnf = status === "dnf", before = status === "read_before";

  // The pace you'd need from here to make the next landmark (or the target) on time.
  let pace: string | null = null;
  if (status === "reading" && !plan.ended) {
    const next = plan.landmarks.find((l) => !l.beyond && l.percent > percent);
    const target = next ? next.percent : plan.toPercent;
    const dateIso = next ? next.date : plan.deadline;
    if (dateIso) {
      const days = Math.max(1, Math.ceil((new Date(dateIso).getTime() - Date.now()) / DAY));
      if (plan.todayPercent !== null && percent >= plan.todayPercent) pace = "ahead of plan";
      else if (percent < target) pace = `${paceLabel((target - percent) / days, ctx)} to stay on plan`;
    }
  }
  const w = whereParts(percent, ctx);

  return (
    <div className={`you${open ? " open" : ""}`}>
      <div className="me-row">
        <Avatar name={me.name} image={me.image} />
        <div style={{ minWidth: 0 }}>
          {done ? <div className="where"><b className="ok"><CheckIcon /> Finished</b><span>{statusAt ? dateShort(statusAt) : `${percent}%`}</span></div>
            : dnf ? <div className="where"><b>Stopped at {percent}%</b><span>done with it · everything's open now</span></div>
            : before ? <div className="where"><b>Read it before</b><span>sitting it out · nothing is sealed for you</span></div>
            : <div className="where"><b>{w.lead}</b><span>{w.rest}{mine?.label ? ` · ${mine.label}` : ""}</span></div>}
          {!open && pace && <div className="pace">{pace}</div>}
          {!open && !pace && mine?.source === "hardcover" && <div className="pace muted"><SyncIcon width={12} height={12} /> synced from Hardcover{mine.updatedAt ? ` · ${ago(mine.updatedAt)}` : ""}</div>}
        </div>
        {before ? <button type="button" className="btn ghost sm upd" disabled={rereading.isPending} onClick={() => rereading.mutate(true)}>Reading it after all</button>
          : !dnf && <button type="button" className="btn ghost sm upd" aria-expanded={open} onClick={() => setOpen((o) => !o)}>{open ? "Close" : "Update"}</button>}
      </div>
      {rereading.isError && <p className="error small" style={{ margin: "0 12px 8px" }}>{describeError(rereading.error)}</p>}
      {open && (
        <ProgressEditor clubId={clubId} book={book} percent={percent} myFormat={ctx.myFormat ?? (ctx.pages ? "pages" : "percent")} source={mine?.source ?? null} done={done}
          onClose={() => setOpen(false)} onSaved={() => setOpen(false)}
          sitOut={book.readBefore && book.readBefore.mine !== "unread" ? () => rereading.mutate(false) : undefined} />
      )}
      <NoteLine clubId={clubId} bookId={book.id} count={book.myNotes ?? 0} myProgress={percent} ctx={{ ...ctx, landmarks: plan.landmarks }} canForward={status === "reading" || done} />
    </div>
  );
}

/** One-line private capture, the last line of your block; the count opens the notebook. */
function NoteLine({ clubId, bookId, count, myProgress, ctx, canForward }: { clubId: string; bookId: string; count: number; myProgress: number; ctx: PosCtx; canForward: boolean }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [forward, setForward] = useState(false);
  const [body, setBody] = useState("");
  const [saved, setSaved] = useState(false);
  const save = useMutation({
    mutationFn: () => api.addNote(clubId, bookId, { body, position: myProgress }),
    onSuccess: async () => { setBody(""); setSaved(true); setTimeout(() => setSaved(false), 1500); await qc.invalidateQueries({ queryKey: ["books", clubId] }); await qc.invalidateQueries({ queryKey: ["notes", clubId, bookId] }); },
  });
  return (
    <form className="nb-line" onSubmit={(e) => { e.preventDefault(); if (body.trim()) save.mutate(); }}>
      <NoteIcon width={16} height={16} />
      <input id="note-to-self" className="txt" value={body} maxLength={4000} placeholder={saved ? "Saved." : "Note to self…"} onChange={(e) => setBody(e.target.value)} aria-label="Note to self" />
      {body.trim() ? (
        <button type="submit" className="btn primary sm" disabled={save.isPending}>Save</button>
      ) : (
        <>
          {canForward && myProgress > 0 && <button type="button" className="fwd" aria-label="Leave a note forward" title="Leave a note forward" onClick={() => setForward(true)}><Envelope /></button>}
          <button type="button" className="cnt" onClick={() => void navigate({ to: "/clubs/$clubId/notes/$bookId", params: { clubId, bookId } })}>{count > 0 ? `${count} ${count === 1 ? "note" : "notes"}` : "notebook"} ›</button>
        </>
      )}
      {forward && <LeaveNoteSheet clubId={clubId} bookId={bookId} ctx={ctx} myPercent={myProgress} onClose={() => setForward(false)} />}
      {save.isError && <span className="error small">{describeError(save.error)}</span>}
    </form>
  );
}

// ---- the track: everyone on one line

const PILE_PX = 34; // faces closer than this merge into a pile
const LIST_FIRST = 5;

/** Where a reader sits on the track: sitters-out at the start, finishers at the end. */
const posOf = (p: ProgressEntry) => (p.status === "read_before" ? 0 : p.status === "finished" ? 100 : p.percent);
const isDone = (p: ProgressEntry) => p.status === "finished";

type Group = { key: string; x: number; members: ProgressEntry[] };

function Track({ entries, meId, plan, pages }: { entries: ProgressEntry[]; meId?: string; plan: PlanView; pages: number | null }) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(600);
  const [sel, setSel] = useState<string | null>(null); // a group key
  const [all, setAll] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth || 600)); ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const mine = entries.find((p) => p.id === meId) ?? null;
  // Everyone else, sorted along the track and piled where they would overlap. You stay on top at your own spot.
  const groups = useMemo<Group[]>(() => {
    const others = entries.filter((p) => p.id !== meId).sort((a, b) => posOf(a) - posOf(b) || a.name.localeCompare(b.name));
    const out: Group[] = [];
    for (const p of others) {
      const x = (posOf(p) / 100) * width;
      const last = out.at(-1);
      if (last && x - last.x < PILE_PX && isDone(p) === isDone(last.members[0]!)) { last.members.push(p); continue; }
      out.push({ key: p.id, x, members: [p] });
    }
    return out;
  }, [entries, meId, width]);
  useEffect(() => { if (sel && !groups.some((g) => g.key === sel)) setSel(null); }, [groups, sel]);
  const selected = groups.find((g) => g.key === sel) ?? null;
  const pct = (x: number) => `${(x / Math.max(1, width)) * 100}%`;
  const whenOf = (p: ProgressEntry) => p.status === "finished" ? `finished${p.updatedAt ? ` ${dateShort(p.updatedAt)}` : ""}` : p.status === "dnf" ? `stopped at ${p.percent}%` : p.status === "read_before" ? "read it before" : p.label ?? (p.source === "hardcover" ? `via Hardcover · ${ago(p.updatedAt)}` : p.updatedAt ? ago(p.updatedAt) : "not started");
  const pageOf = (p: ProgressEntry) => pages && p.status === "reading" && p.percent > 0 ? `p. ${pageAt(p.percent, pages)}` : null;
  const landmarkOf = (percent: number) => [...plan.landmarks].filter((l) => !l.beyond && l.percent <= percent).at(-1)?.name ?? null;

  return (
    <>
      <div className={`track${plan.todayPercent !== null ? " has-cap" : ""}`} ref={ref} aria-label="Where everyone else is">
        {mine && mine.status === "reading" && <div className="fill" style={{ width: `${mine.percent}%` }} />}
        {plan.landmarks.filter((l) => !l.beyond).map((l) => <span key={l.id} className="lm" style={{ left: `${l.percent}%` }}><b>{l.name}</b></span>)}
        {plan.todayPercent !== null && <>
          <span className="today" style={{ left: `${plan.todayPercent}%` }} title="Where the plan is today" />
          {/* Says what the tick is in plain words: the page a reader is at today if they finish right on the deadline. Kept inside the track's edges. */}
          <span className="today-cap" style={{ "--x": `${plan.todayPercent}%` } as React.CSSProperties}>{pages ? `p. ${pageAt(plan.todayPercent, pages)} today` : "here today"}{plan.deadline ? ` to finish by ${dateShort(plan.deadline)}` : ""}</span>
        </>}
        {groups.map((g) => {
          const one = g.members.length === 1 ? g.members[0]! : null;
          const open = sel === g.key;
          const label = one ? `${one.name} · ${whenOf(one)}` : `${g.members.length} readers`;
          return (
            <button key={g.key} type="button" className={`${one ? "who" : "pile"}${open ? " sel" : ""}${one && one.status === "read_before" ? " off" : ""}${g.members.every(isDone) ? " done" : ""}`}
              style={{ "--x": pct(g.x) } as React.CSSProperties} aria-label={label} aria-expanded={open} onClick={() => { setAll(false); setSel(open ? null : g.key); }}>
              {g.members.slice(0, 2).map((p) => <Avatar key={p.id} name={p.name} image={p.image} />)}
              {!one && <u>{g.members.length}</u>}
              {one && <i className="tip">{one.name}</i>}
            </button>
          );
        })}
        {mine && (
          <span className={`who me${mine.status === "read_before" ? " off" : ""}${mine.status === "finished" ? " done" : ""}`} style={{ "--x": `${posOf(mine)}%` } as React.CSSProperties} title="You">
            <Avatar name={mine.name} image={mine.image} />
          </span>
        )}
      </div>
      {selected && selected.members.length === 1 && (() => { const p = selected.members[0]!; const pg = pageOf(p); const lm = p.status === "reading" ? landmarkOf(p.percent) : null;
        const detail = p.status === "reading"
          ? [pg ? `${pg} of ${pages}` : null, `${p.percent}%`, lm ? `past ${lm}` : null, p.label ?? (p.source === "hardcover" ? `via Hardcover · ${ago(p.updatedAt)}` : p.updatedAt ? ago(p.updatedAt) : "not started")].filter(Boolean).join(" · ")
          : whenOf(p);
        return (
        <div className="sel-row">
          <Avatar name={p.name} image={p.image} />
          <div style={{ minWidth: 0 }}>
            <div className="n">{p.name}</div>
            <div className="d">{detail}</div>
          </div>
        </div>
      ); })()}
      {selected && selected.members.length > 1 && (() => {
        const ms = [...selected.members].sort((a, b) => posOf(b) - posOf(a));
        const shown = all ? ms : ms.slice(0, LIST_FIRST);
        const done = ms.every(isDone);
        const lo = Math.min(...ms.map(posOf)), hi = Math.max(...ms.map(posOf));
        const head = done ? `${ms.length} finished` : pages ? `${ms.length} readers around p. ${pageAt(lo, pages)} to p. ${pageAt(hi, pages)}` : `${ms.length} readers around ${lo}% to ${hi}%`;
        const lm = done ? null : landmarkOf(hi);
        return (
          <div className="sel-list">
            <div className="hd"><span>{head}</span>{lm && <span>past {lm}</span>}</div>
            {shown.map((p) => (
              <div key={p.id} className="m"><Avatar name={p.name} image={p.image} /><span style={{ minWidth: 0 }}>{p.name}<span className="s">{whenOf(p)}</span></span><span className="p">{p.status === "finished" || p.status === "read_before" ? <CheckIcon /> : p.status === "dnf" ? "—" : `${p.percent}%`}</span></div>
            ))}
            {!all && ms.length > LIST_FIRST && <button type="button" className="more" onClick={() => setAll(true)}>{ms.length - LIST_FIRST} more ›</button>}
          </div>
        );
      })()}
    </>
  );
}

function AdminMenu({ clubId, book, plan, meetingAt, usualPace }: { clubId: string; book: ClubBook; plan: PlanView; meetingAt: string | null; usualPace: number | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<"finish" | "shelve" | "dates" | "plan" | null>(null);
  const ref = useRef<HTMLDivElement>(null);
  const refresh = () => qc.invalidateQueries({ queryKey: ["books", clubId] });
  const finish = useMutation({ mutationFn: () => api.setBookStatus(clubId, book.id, "finished"), onSuccess: refresh });
  const shelve = useMutation({ mutationFn: () => api.setBookStatus(clubId, book.id, "on_deck"), onSuccess: refresh });
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);
  return (
    <div className="hero-menu" ref={ref}>
      <button type="button" className="more" aria-label="Book actions" aria-expanded={open} onClick={() => setOpen((o) => !o)}><MoreIcon /></button>
      {open && (
        <div className="menu" role="menu">
          <button role="menuitem" onClick={() => { setOpen(false); setConfirm("finish"); }}>Finish this book</button>
          <button role="menuitem" onClick={() => { setOpen(false); setConfirm("shelve"); }}>Put back on deck</button>
          <button role="menuitem" onClick={() => { setOpen(false); setConfirm("dates"); }}>Edit start date</button>
          <button role="menuitem" onClick={() => { setOpen(false); setConfirm("plan"); }}>{plan.ended ? `Plan this stretch · from p. ${plan.to}` : plan.custom ? "Edit the plan" : "Plan the read"}</button>
        </div>
      )}
      {confirm === "plan" && <PlanSheet clubId={clubId} book={book} plan={plan} meetingAt={meetingAt} usualPace={usualPace} onClose={() => setConfirm(null)} />}
      {confirm === "dates" && (
        <div className="hero-confirm" style={{ padding: 0, border: 0 }}>
          <DatesEditor clubId={clubId} book={book} onDone={() => setConfirm(null)} />
        </div>
      )}
      {confirm && confirm !== "dates" && confirm !== "plan" && (
        <div className="confirm hero-confirm">
          <p className="small">{confirm === "finish" ? `Move ${book.book.title} to Finished? Everyone can still update progress and read the discussion, and ratings open.` : "Shelve it for now? Progress is kept and it can be started again later."}</p>
          <div className="row">
            <button className="btn primary sm" disabled={finish.isPending || shelve.isPending} onClick={() => (confirm === "finish" ? finish : shelve).mutate()}>Yes</button>
            <button className="btn ghost sm" onClick={() => setConfirm(null)}>Cancel</button>
          </div>
          {(finish.isError || shelve.isError) && <p className="error">{describeError(finish.error ?? shelve.error)}</p>}
        </div>
      )}
    </div>
  );
}

function StillTalking({ clubId, t }: { clubId: string; t: import("@pagemates/shared").RecentTalk }) {
  return (
    <Link to="/clubs/$clubId/talk" params={{ clubId }} search={{ book: t.clubBookId }} className="quiet link">
      {t.coverUrl ? <span className="cover sm" style={{ width: 26, height: 39 }}><img src={t.coverUrl} alt="" /></span> : <TalkIcon />}
      <div style={{ minWidth: 0 }}>
        <b>Still talking about {t.title}</b>
        <div className="d">{t.unread > 0 ? `${t.unread} new · ` : ""}last {ago(t.lastAt)}</div>
      </div>
      <ChevronIcon className="go" />
    </Link>
  );
}

function TalkTeaser({ clubId, bookId }: { clubId: string; bookId: string }) {
  const d = useQuery({ queryKey: ["discussion", clubId, bookId], queryFn: () => api.discussion(clubId, bookId), staleTime: 30_000 });
  if (!d.data) return null;
  const t = d.data;
  const last = [...t.comments].reverse().find((c) => !c.removed);
  const lastAt = last ? (last.lastReplyAt && last.lastReplyAt > last.createdAt ? last.lastReplyAt : last.createdAt) : null;
  return (
    <Link to="/clubs/$clubId/talk" params={{ clubId }} className="quiet link">
      <TalkIcon />
      <div>
        <b className={t.total === 0 ? undefined : "n"}>{t.total === 0 ? "No messages yet" : `${t.total} ${t.total === 1 ? "message" : "messages"}`}</b>
        <div className="d">{t.total === 0 ? "Start the conversation" : `${t.lockedCount > 0 ? `${t.lockedCount} sealed ahead of you` : "nothing sealed for you"}${lastAt ? ` · last ${ago(lastAt)}` : ""}`}</div>
      </div>
      <ChevronIcon className="go" />
    </Link>
  );
}

/**
 * Nothing being read: the lamp over one empty-state card. Admins pick the
 * book straight from the catalog (it starts at once, whatever the club's
 * picking method); members suggest one, or wait when only admins can.
 */
function EmptyReading({ clubId, isAdmin, canSuggest, first, deck }: { clubId: string; isAdmin: boolean; canSuggest: boolean; first: boolean; deck: number }) {
  const [picking, setPicking] = useState(false);
  const lamp = <div className="es-lamp"><div className="glow" aria-hidden="true" /><LampArt /></div>;
  const onDeckLink = deck > 0
    ? <Link to="/clubs/$clubId/shelf" params={{ clubId }}>Or choose from the {deck} on deck</Link>
    : null;
  if (isAdmin) {
    return (
      <>
        <EmptyState className="nightstand" art={lamp} title="Nothing on the nightstand yet"
          action={<button type="button" className="btn primary" onClick={() => setPicking(true)}>{first ? "Pick the first book" : "Pick the next book"}</button>}
          secondary={onDeckLink}>
          {first ? "Pick the club's first book and everyone's progress shows up here." : "Pick the club's next book and everyone's progress shows up here."}
        </EmptyState>
        {picking && <PickBook clubId={clubId} mode="current" first={first} onClose={() => setPicking(false)} />}
      </>
    );
  }
  return (
    <EmptyState className="nightstand" art={lamp} title={first ? "Waiting for the first book" : "Waiting for the next book"}
      action={canSuggest ? <Link to="/clubs/$clubId/suggestions" params={{ clubId }} search={{ add: true }} className="btn primary">Suggest a book</Link> : undefined}>
      {canSuggest ? "When an admin picks it, everyone's progress shows up here." : "An admin will pick the first book."}
    </EmptyState>
  );
}
